import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Star,
  Check,
  X,
  Loader2,
  Send,
  ArrowUp,
  ArrowDown,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Review {
  id: string;
  user_id: string;
  rating: number;
  review_text: string;
  display_name: string;
  display_role: string | null;
  photo_url: string | null;
  status: "pending" | "approved" | "rejected";
  is_featured: boolean;
  featured_order: number;
  credits_granted: boolean;
  created_at: string;
  reviewed_at: string | null;
}

type Filter = "all" | "pending" | "approved" | "rejected";

export function ReviewsManager() {
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("pending");
  const [detailReview, setDetailReview] = useState<Review | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("reviews")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Failed to load reviews", description: error.message, variant: "destructive" });
    } else {
      setReviews((data as Review[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = reviews.filter((r) => (filter === "all" ? true : r.status === filter));

  const updateReview = async (id: string, patch: Partial<Review>) => {
    const { error } = await supabase
      .from("reviews")
      .update({ ...patch, reviewed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    await load();
  };

  const setStatus = (r: Review, status: Review["status"]) =>
    updateReview(r.id, { status, ...(status !== "approved" ? { is_featured: false } : {}) });

  const toggleFeatured = (r: Review) => updateReview(r.id, { is_featured: !r.is_featured });

  const moveOrder = async (r: Review, dir: -1 | 1) => {
    const newOrder = (r.featured_order ?? 0) + dir;
    await updateReview(r.id, { featured_order: newOrder });
  };

  const openBulkDialog = async () => {
    setBulkOpen(true);
    setBulkLoading(true);
    setEligibleCount(null);
    const { data, error } = await supabase.functions.invoke("bulk-invite-existing-reviews", {
      body: { dry_run: true },
    });
    setBulkLoading(false);
    if (error || data?.error) {
      toast({
        title: "Could not load count",
        description: data?.error || error?.message || "",
        variant: "destructive",
      });
      return;
    }
    setEligibleCount(data?.eligible_count ?? 0);
  };

  const runBulkSend = async () => {
    setBulkSending(true);
    const { data, error } = await supabase.functions.invoke("bulk-invite-existing-reviews", {
      body: {},
    });
    setBulkSending(false);
    if (error || data?.error) {
      toast({
        title: "Bulk send failed",
        description: data?.error || error?.message || "",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Invitations sent", description: `Sent: ${data.sent}, Failed: ${data.failed}` });
    setBulkOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="rounded-xl border border-border/60 bg-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending ({reviews.filter((r) => r.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({reviews.filter((r) => r.status === "approved").length})
            </TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button variant="outline" size="sm" onClick={openBulkDialog} className="gap-2">
          <Send className="h-4 w-4" /> Send invitation to existing users
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">No reviews in this view.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Preview</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.photo_url ? (
                        <img
                          src={r.photo_url}
                          alt={r.display_name}
                          className="w-9 h-9 rounded-full object-cover border border-border"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                          {r.display_name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{r.display_name}</div>
                      <div className="text-xs text-muted-foreground">{r.display_role || "—"}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={cn(
                              "w-3.5 h-3.5",
                              i < r.rating
                                ? "fill-warning text-warning"
                                : "text-muted-foreground/30"
                            )}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-sm truncate">{r.review_text}</p>
                    </TableCell>
                    <TableCell>
                      {r.status === "pending" && <Badge variant="secondary">Pending</Badge>}
                      {r.status === "approved" && (
                        <Badge className="bg-success/15 text-success border border-success/30 hover:bg-success/20">
                          Approved
                        </Badge>
                      )}
                      {r.status === "rejected" && <Badge variant="destructive">Rejected</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={r.is_featured}
                          disabled={r.status !== "approved"}
                          onCheckedChange={() => toggleFeatured(r)}
                        />
                        {r.is_featured && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">#{r.featured_order}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => moveOrder(r, -1)}
                            >
                              <ArrowUp className="w-3 h-3" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => moveOrder(r, 1)}
                            >
                              <ArrowDown className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDetailReview(r)}
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {r.status !== "approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setStatus(r, "approved")}
                          title="Approve"
                        >
                          <Check className="w-4 h-4 text-success" />
                        </Button>
                      )}
                      {r.status !== "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setStatus(r, "rejected")}
                          title="Reject"
                        >
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detailReview} onOpenChange={(o) => !o && setDetailReview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review detail</DialogTitle>
            <DialogDescription>Full content as submitted by the user.</DialogDescription>
          </DialogHeader>
          {detailReview && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {detailReview.photo_url ? (
                  <img
                    src={detailReview.photo_url}
                    alt={detailReview.display_name}
                    className="w-16 h-16 rounded-full object-cover border border-border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center font-semibold">
                    {detailReview.display_name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-semibold">{detailReview.display_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {detailReview.display_role || "—"}
                  </div>
                  <div className="flex gap-0.5 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          "w-4 h-4",
                          i < detailReview.rating
                            ? "fill-warning text-warning"
                            : "text-muted-foreground/30"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm whitespace-pre-wrap">{detailReview.review_text}</p>
              <div className="text-xs text-muted-foreground">
                Submitted {new Date(detailReview.created_at).toLocaleString()} · Credits granted:{" "}
                {detailReview.credits_granted ? "yes" : "no"}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send review invitation to existing users</DialogTitle>
            <DialogDescription>
              This sends a one-time invitation email to every non-admin user who has used at least
              one credit and hasn't been invited or reviewed yet.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {bulkLoading ? (
              <div className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Counting eligible users…
              </div>
            ) : eligibleCount !== null ? (
              <p className="text-sm">
                Eligible recipients: <span className="font-semibold">{eligibleCount}</span>
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={runBulkSend}
              disabled={bulkSending || eligibleCount === 0 || eligibleCount === null}
            >
              {bulkSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                </>
              ) : (
                `Send to ${eligibleCount ?? 0} user${eligibleCount === 1 ? "" : "s"}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
